import { useMutation, useQueryClient } from 'react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ProspectPaymentFormProps,
  RegisterNAFProspectQueryParams,
  RegisterNAFProspectQueryResponse,
} from './ProspectPaymentForm.types';
import {
  CHECK_CODE_QUERY_KEY,
  REGISTER_NAF_PROSPECT_QUERY_KEY,
  VALIDATE_PAYMENT_QUERY_KEY,
  checkCodeQuery,
  registerNAFProspectQuery,
  validatePaymentQuery,
} from './ProspectPaymentForm.query';
import { useActiveNafList } from '../../../../../../../../hooks/fetchers/useActiveNafList/useActiveNafList';
import { usePlansENT } from '../../../../../../../../hooks/fetchers/usePlansENT/usePlansENT';
import { SelectOption } from '../../../../../../../commons/Select/Select.types';
import { Code } from '../../../../../../../../types/models/code/code.types';
import { amountWithTVA } from '../../../../../../../../utils/helpers/price/amountWithTVA';
import Select from '../../../../../../../commons/Select/Select';
import Input from '../../../../../../../commons/Input/Input';
import Button from '../../../../../../../designSystem/Button/Button';
import { ButtonColor, ButtonSize } from '../../../../../../../designSystem/Button/Button.types';
import ConfirmationBisRow from '../../../../../../authentication/RegisterSteps/RegisterConfirmationBisStep/RegisterConfirmationBisStepForm/ConfirmationBisRow/ConfirmationBisRow';
import { useFullCompany } from '../../../../../../../../hooks/fetchers/useFullCompany/useFullCompany';
import { PaymentTypes } from '../../../../../../../../types/models/receipt/receipt.types';
import { BaseResponse } from '../../../../../../../../types/api/api.types';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { generateRandomTransId } from '../../../../../../../../utils/helpers/numbers/generateRandomTransId';

export default ({ prospect }: ProspectPaymentFormProps) => {

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: NAFList } = useActiveNafList();

  const { data: plansENT } = usePlansENT();

  const { data: fullCompany, refetch } = useFullCompany(prospect?.id_user || 0, { enabled: false });

  useEffect(() => {
    if (prospect) {
      refetch()
    }
  }, [prospect])

  useEffect(() => {
    console.log('fullCompany', fullCompany)
  }, [fullCompany])

  const [choosedNAFCodes, setChoosedNAFCodes] = useState<(SelectOption | null)[]>([null]);
  const [duration, setDuration] = useState<1 | 2 | 3>();
  const [codeInputValue, setCodeInputValue] = useState<string>('');
  const [code, setCode] = useState<Code>();
  const [isDomainSubmitted, setDomainSubmitted] = useState<boolean>(false);

  const [bankCode, setBankCode] = useState(generateRandomTransId().toString())
  const [paymentType, setPaymentType] = useState(PaymentTypes.CB)

  const [transId, setTransId] = useState<string>()

  const currentTime = useMemo(() => moment().utc().format('YYYYMMDDHHmmss'), []);

  const onChange = useCallback(
    (indexToUpdate: number, data: SelectOption) => {
      const updatedItems = choosedNAFCodes.reduce(
        (acc, item, index) => {
          if (index === indexToUpdate) {
            return [...acc, data];
          } else {
            return [...acc, item];
          }
        },
        [] as (SelectOption | null)[]
      );
      setChoosedNAFCodes(updatedItems);
    },
    [choosedNAFCodes]
  );

  const onAdd = useCallback(() => {
    setChoosedNAFCodes([...choosedNAFCodes, null]);
  }, [choosedNAFCodes]);

  const onRemove = useCallback(
    (indexToRemove: number) => {
      const newItems = choosedNAFCodes.reduce(
        (acc, item, index) => {
          if (index !== indexToRemove) {
            return [...acc, item];
          } else {
            return acc;
          }
        },
        [] as (SelectOption | null)[]
      );
      setChoosedNAFCodes(newItems);
    },
    [choosedNAFCodes]
  );

  const currentPlan = useMemo(
    () => plansENT?.find((plan) => plan.id === parseInt(fullCompany?.id_plan.toString() || '0')),
    [plansENT, fullCompany]
  );

  const NAFCodes = useMemo(() => choosedNAFCodes.map((item) => item?.value), [choosedNAFCodes]);

  const isValid = useMemo(() => NAFCodes.filter((item) => !item)?.length === 0, [NAFCodes]);

  const amount = useMemo(() => {
    console.log({NAFCodes, currentPlan, duration})
    if (NAFCodes && NAFCodes.length > 3 && currentPlan && duration) {
      return 30 * (NAFCodes.length - 3) + currentPlan[`price_${duration}y`] / 100;
    } else if (currentPlan && duration) {
      return currentPlan[`price_${duration}y`] / 100;
    } else {
      return null;
    }
  }, [NAFCodes, currentPlan, duration]);

  const amountWithDiscount = useMemo(() => {
    if (amount && code) {
      return amount - amount * (code.percentage / 100);
    }
  }, [amount, code, duration]);

  // const updatedAmountTTC = useMemo(
  //   () => (amountWithDiscount ? amountWithTVA(amountWithDiscount) : amountWithTVA(amount || 0)),
  //   [amount, amountWithDiscount]
  // );

  const { mutate: registerNAF } = useMutation<
    Awaited<RegisterNAFProspectQueryResponse>,
    Error,
    RegisterNAFProspectQueryParams
  >((data) => registerNAFProspectQuery(data), {
    mutationKey: REGISTER_NAF_PROSPECT_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: ({ transId }) => {
      setDomainSubmitted(true)
      setTransId(transId)
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const { mutate: checkCode } = useMutation<Awaited<Code>, Error, string>(
    (data) => checkCodeQuery(data),
    {
      mutationKey: CHECK_CODE_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: async (code) => {
        setCode(code);
      },
      onError: async (error) => {
        alert(error.message);
      },
    }
  );

  const {
    mutate: validatePayment,
    isLoading: isValidateLoading
  } = useMutation<Awaited<BaseResponse>, Error, any>(
    (data) => validatePaymentQuery(data),
    {
      mutationKey: VALIDATE_PAYMENT_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: () => {
        // props.setOpen(false)
        navigate(0)
      },
      onError: (error) => {
        alert(error.message)
      },
    }
  )

  return (
    <>
      <form className="form form--scrollable" style={{ width: 'calc(100% - var(--spacing-mToL))', padding: 'var(--spacing-mToL)' }}>
        <div style={{ marginBottom: 'var(--spacing-sToM)' }}>
          <Select
            placeholder={'Sélectionnez la durée de votre abonnement'}
            label="Durée de l'abonnement"
            id="subscription"
            required
            options={[
              {
                value: '1',
                label: "1 an d'abonnement",
              },
              {
                value: '2',
                label: "2 ans d'abonnement (le plus utilisé)",
              },
              {
                value: '3',
                label: "3 ans d'abonnement (le plus économique)",
              },
            ]}
            // @ts-ignore
            onChange={(selectedOption: SelectOption) => {
              const value = parseInt(selectedOption.value);
              if (value === 1 || value === 2 || value === 3) {
                setDuration(value);
              }
            }}
          />
          {duration && duration > 1 && currentPlan && (
            <span className="register__payment-save">
              Vous économisez{' '}
              {(currentPlan.price_1y * duration - currentPlan[`price_${duration}y`]) / 100} €
            </span>
          )}
        </div>
        {choosedNAFCodes.map((_, index) => (
          <ConfirmationBisRow
            key={index}
            isLast={index === choosedNAFCodes.length - 1}
            {...{ index, onChange, onAdd, onRemove, NAFList, choosedNAFCodes }}
          />
        ))}
        <div className="form__row register__confirmation-bis-row register__payment-code">
          <div style={{ flex: 1 }} className="flex-column">
            <Input
              name="code"
              label="Code promo"
              onChange={(e) => setCodeInputValue(e.target.value)}
              {...(code && {
                divProps: {
                  style: {
                    marginBottom: 0,
                  },
                },
              })}
            />
            {code && <span className="register__payment-save">{code.description}</span>}
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              checkCode(codeInputValue);
            }}>
            Ajouter
          </button>
        </div>
        {amount && (
          <div className="register__payment-price">
            <div className="register__payment-price-row">
              <span>Abonnement annuel</span>
              <span>{amount?.toFixed(2)}€</span>
            </div>
            {NAFCodes.length > 3 && (
              <div className="register__payment-price-row">
                <span>Domaines supplémentaires</span>
                <span>{NAFCodes.length - 3}</span>
              </div>
            )}
            <div className="register__payment-price-row">
              <span>TVA 20%</span>
              <span>{(amountWithTVA(amount) - amount).toFixed(2)}€</span>
            </div>
            {code && (
              <div className="register__payment-price-row">
                <span style={{ color: 'var(--success)' }}>
                  Code promo: <strong>{code.code}</strong>
                </span>
                <span style={{ color: 'var(--success)' }}>-{code.percentage}%</span>
              </div>
            )}
            <div className="register__payment-price-row register__payment-price-row--total">
              <span>Total TTC</span>
              {amountWithDiscount ? (
                <>
                  <span style={{ color: 'var(--success)' }}>
                    {amountWithTVA(amountWithDiscount).toFixed(2)}€{' '}
                    <span style={{ color: 'red', textDecoration: 'line-through' }}>
                      {amountWithTVA(amount).toFixed(2)}€
                    </span>
                  </span>
                </>
              ) : (
                <span>{amountWithTVA(amount).toFixed(2)}€</span>
              )}
            </div>
          </div>
        )}
        {/* <Button
          style={{ alignSelf: 'unset' }}
          onClick={(e) => {
            e.preventDefault();
            mutate(NAFCodes);
          }}
          size={ButtonSize.Large}
          disabled={!isValid || !amount || !duration}
          color={ButtonColor.Secondary}>
          {`Payer · ${
            amountWithDiscount
              ? amountWithTVA(amountWithDiscount).toFixed(2)
              : amountWithTVA(amount || 0).toFixed(2)
          }€`}
        </Button> */}
        {
          !isDomainSubmitted && !transId
            ?
            <Button
              style={{ alignSelf: 'unset' }}
              onClick={(e) => {
                e.preventDefault();
                if (prospect && duration) {
                  registerNAF({
                    prospectId: prospect.id,
                    fieldsOfActivity: NAFCodes,

                    id_user: prospect.id_user,
                    id_plan: currentPlan?.id || 0,
                    code,
                    currentTime,
                    duration
                  });
                }
              }}
              size={ButtonSize.Large}
              disabled={!isValid || !amount || !duration}
              color={ButtonColor.Secondary}>
              Valider
            </Button>
            :
            <div className='flex-column'>
              <Select 
                defaultValue={{
                  value: PaymentTypes.CB,
                  label: PaymentTypes.CB
                }}
                options={Object.values(PaymentTypes).map(item => ({
                  value: item,
                  label: item
                }))}
                // @ts-ignore
                onChange={({ value }: SelectOption) => setPaymentType(value as PaymentTypes)}
                divProps={{
                  style: {
                    minWidth: 150
                  }
                }}
              />
              {paymentType === PaymentTypes.CB && 
                <Input 
                  placeholder="Code banque" 
                  max={6} 
                  defaultValue={bankCode}
                  onChange={(e) => setBankCode(e.target.value)}
                />
              }
              <Button 
                onClick={(e) => {
                  e.preventDefault()
                  validatePayment({
                    vads_cust_id: `ENT${prospect?.id_user}`,
                    vads_trans_id: transId,
                    vads_trans_date: currentTime,
                    vads_trans_status: 'AUTHORISED',
        
                    bankCode,
                    paymentType
                  })
                }} 
                style={{ marginTop: 'var(--spacing-sToM)' }}
                isLoading={isValidateLoading}
                color={ButtonColor.Secondary}
                >
                  Valider le paiement manuel
                </Button>
            </div>
        }
      </form>
    </>
  );
};
